import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { checkIsSelectedOrganizationOwn, getAUser, getBundleById, getOrganizationHierarchyForAdmin, getWebshopState } from '../../../../utils';
import {
  authSetSelectedOrganization,
  shopClearOrderForOrganization,
  shopClearOrderOnBehalfUser,
  shopResetProduct,
  shopSetOrderForOrganization,
  shopSetOrderOnBehalfUser,
  shopSetProduct,
  showGrowlNotification,
} from '../../../../store/actions';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../../store/reducers';
import { useTranslation } from 'react-i18next';
import { Loader, Page } from '@gaiads/telia-react-component-library';

const WebshopCheckout: FunctionComponent = () => {
  const history = useHistory();
  const { stateId } = useParams<{ stateId?: string }>();
  const dispatch = useDispatch();

  const { t } = useTranslation('shop');

  const user = useSelector((state: RootState) => state.authentication.user);
  const ownOrganizationId = useSelector((state: RootState) => state.authentication.user?.organization?.id);

  const [loadingWebshopState, setLoadingWebshopState] = useState(false);
  const [webshopStateRecalled, setWebshopStateRecalled] = useState(false);
  const [webshopStateRecallFailed, setWebshopStateRecallFailed] = useState(false);

  useEffect(() => {
    if (stateId && !loadingWebshopState && !webshopStateRecalled && !webshopStateRecallFailed) {
      setLoadingWebshopState(true);

      dispatch(shopResetProduct());
      dispatch(shopClearOrderOnBehalfUser());
      dispatch(shopClearOrderForOrganization());

      getWebshopState(stateId)
        .then(response => {
          if (response) {
            return typeof response.bundle_id === 'number' && typeof response.main_item_variant_id === 'number'
              ? Promise.resolve(response)
              : Promise.reject('Bundle id or main item variant id missing in webshop state');
          } else {
            return Promise.reject('Fetching webshop state failed');
          }
        })
        .then(async response => {
          if (!checkIsSelectedOrganizationOwn(ownOrganizationId, response.organization_id)) {
            const organizations = await getOrganizationHierarchyForAdmin();
            const matchingOrganization = organizations?.find(org => org.id === response.organization_id);
            if (!matchingOrganization) {
              return Promise.reject('Organization in webshop state not found in organization hierarchy');
            }
            dispatch(authSetSelectedOrganization(matchingOrganization));
          }
          return Promise.resolve(response);
        })
        .then(async response => {
          if (response.created_for_user_id && user?.id !== response.created_for_user_id) {
            const orderOnBehalfUser = await getAUser(response.created_for_user_id);
            if (!orderOnBehalfUser) {
              return Promise.reject('User in webshop state not found');
            }
            dispatch(shopSetOrderOnBehalfUser(orderOnBehalfUser));
          } else if (!response.created_for_user_id) {
            dispatch(shopSetOrderForOrganization());
          }
          return Promise.resolve(response);
        })
        .then(async response => {
          const bundle = await getBundleById(`${response.bundle_id}`);
          if (!bundle) {
            return Promise.reject('Bundle in webshop state not found');
          }
          dispatch(
            shopSetProduct({
              ...bundle,
              chosenMainItemVariantId: response.main_item_variant_id!,
            }),
          );
          setWebshopStateRecalled(true);
        })
        .catch(error => {
          console.error('An error occurred during webshop state recall', error);
          setWebshopStateRecallFailed(true);
        })
        .finally(() => setLoadingWebshopState(false));
    }
  }, [dispatch, loadingWebshopState, ownOrganizationId, stateId, user?.id, webshopStateRecalled, webshopStateRecallFailed]);

  useEffect(() => {
    if (webshopStateRecalled) {
      history.replace('/shop/checkout');
    }
  }, [history, webshopStateRecalled]);

  useEffect(() => {
    if (webshopStateRecallFailed) {
      dispatch(showGrowlNotification(t('externalWebshop.webshopStateRecallFailed'), 'error'));
      history.replace('/home');
    }
  }, [dispatch, history, t, webshopStateRecallFailed]);

  return (
    <Page>
      <Page.Row alignTextToCenter margin={{ vertical: 'xxlg' }}>
        <Loader />
      </Page.Row>
    </Page>
  );
};

export default WebshopCheckout;
